/**
 * Cart upsell "cascade slot" controller — v2 (per sztuka, 2026-10-30).
 *
 * The slot itself is rendered server-side in Liquid (snippets/cart-upsell-slot.liquid)
 * inside the cart-drawer / main-cart-items sections, so it survives every cart re-render.
 * This controller only:
 *   1. binds the CTA via event delegation on document (survives innerHTML swaps),
 *   2. plays the "join the set" beat in place (ports CartCascadeSlot.tsx handleAdd) while the
 *      add request is ALREADY in flight — the beat never delays the request,
 *   3. adds the product to the real cart WITH the line property `_upsell_rule` (= rule handle)
 *      and re-renders the sections (mirrors assets/product-complete-set.js for the drawer,
 *      assets/cart.js for the page); adds are queued so two quick clicks on two cards never
 *      swap sections against each other,
 *   4. flashes the graduated cart line, moves focus to it, scrolls the fresh slot into view,
 *   5. executes the correction plan the slot emits (`[data-cart-upsell-plan]`): excess promo
 *      units and orphaned promo lines go through /cart/change.js, then the cart is refreshed
 *      and a note is shown,
 *   6. marks cards that are NEW after a re-render (`cart-upsell--enter`) so only those animate.
 *
 * Warstwa kuponów (applyDiscount, strażnik osieroconego kodu) została usunięta: rabat jest
 * automatyczny (funkcja upsell-discount) i nalicza się tylko na linie ze znacznikiem, do
 * wysokości uprawnienia. Cena jest bezpieczna po stronie serwera — to, co robi ten plik,
 * jest wygodą dla klienta, nie zabezpieczeniem pieniędzy.
 */
(function () {
  if (typeof window.routes === 'undefined' || typeof window.fetchConfig !== 'function') return;
  // Guard against double init (the drawer and the cart page can both enqueue this asset).
  if (window.__wingsCartUpsellInit) return;
  window.__wingsCartUpsellInit = true;

  var PUB_SUB = typeof PUB_SUB_EVENTS !== 'undefined'
    ? PUB_SUB_EVENTS
    : { cartUpdate: 'cart-update', cartError: 'cart-error' };

  /* 700 → 420: klient zgłosił, że potwierdzenie „Dodano do zestawu" trzyma za długo
     i koszyk sprawia wrażenie zaciętego — prosił o skrócenie o 40%. Takt NIE opóźnia
     żądania: fetch startuje od razu, a dopiero podmiana sekcji czeka na oba. */
  var ADD_DELAY = 420;
  var lastAddedVariantId = null;
  var lastAddedText = '';

  /* Log atrybucji trzymamy w JS, a nie odczytujemy z DOM przy kazdym dodaniu. Powod: na stronie
     /cart sekcje przychodza z odpowiedzi add-to-cart, czyli sprzed naszego zapisu atrybutu —
     odczyt z DOM dalby wartosc nieaktualna i drugi wpis kaskady nadpisalby pierwszy.
     Liquid sluzy tylko jako zasiew przy wejsciu na strone. */
  var attributionLog = null;

  function isReduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function wait(ms) {
    return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
  }

  function haptic(pattern) {
    try {
      if ('vibrate' in navigator && window.matchMedia('(pointer: coarse)').matches && !isReduced()) {
        navigator.vibrate(pattern);
      }
    } catch (e) {}
  }

  function getInnerHTML(html, selector) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var el = parsed.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  /* Stan „dodano": oferta ↔ potwierdzenie, ikona + ↔ ✓, a NA PRZYCISKU etykieta zmienia się
     na tekst potwierdzenia i przycisk dostaje aria-disabled — wcześniej ✓ stało obok wciąż
     widocznego „Dodaj do zamówienia", a czytnik nie wiedział, że trwa dodawanie. */
  function setAddedState(slot, on) {
    slot.classList.toggle('is-added', on);
    var offer = slot.querySelector('[data-upsell-offer]');
    var added = slot.querySelector('[data-upsell-added]');
    var plus = slot.querySelector('.cart-upsell__add-plus');
    var check = slot.querySelector('.cart-upsell__add-check');
    var spark = slot.querySelector('.cart-upsell__spark');
    var btn = slot.querySelector('[data-upsell-add]');
    var label = slot.querySelector('.cart-upsell__add-label');
    if (offer) offer.hidden = on;
    if (added) added.hidden = !on;
    if (plus) plus.hidden = on;
    if (check) check.hidden = !on;
    if (spark) spark.hidden = !on;
    if (label) {
      if (on) {
        if (!label.dataset.offerText) label.dataset.offerText = label.textContent;
        var confirm = slot.querySelector('.cart-upsell__confirm');
        var addedText = confirm ? confirm.textContent.replace(/\s+/g, ' ').trim() : '';
        if (addedText) label.textContent = addedText;
      } else if (label.dataset.offerText) {
        label.textContent = label.dataset.offerText;
      }
    }
    if (btn) btn.setAttribute('aria-disabled', on ? 'true' : 'false');
    slot.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  function showError(slot, message) {
    var box = slot.querySelector('[data-upsell-error]');
    if (!box) return;
    box.textContent = message || slot.getAttribute('data-error-text') || '';
    box.hidden = !box.textContent;
  }

  function hideError(slot) {
    var box = slot.querySelector('[data-upsell-error]');
    if (!box) return;
    box.hidden = true;
    box.textContent = '';
  }

  function revert(slot) {
    if (!slot) return;
    setAddedState(slot, false);
    delete slot.dataset.busy;
  }

  /* Błąd dodania: komunikat W KARCIE (role=alert), nie `alert()` — modal w koszyku zatrzymywał
     wszystko, a przy błędzie sieci karta wracała do oferty bez słowa. */
  function publishError(slot, variantId, response) {
    if (typeof publish === 'function') {
      publish(PUB_SUB.cartError, {
        source: 'cart-upsell',
        productVariantId: variantId,
        errors: response && (response.errors || response.description),
        message: response && response.message,
      });
    }
    revert(slot);
    showError(slot, response && (response.message || response.description));
  }

  /* Najblizszy przewijalny przodek. Przewijamy JEGO, a nie okno: karta zyje w szufladzie,
     ktora jest `position: fixed`, wiec `scrollIntoView` potrafi przy okazji przesunac
     stronę pod spodem — niewidoczne przy otwartej szufladzie, widoczne po jej zamknieciu. */
  function nearestScrollable(el) {
    var node = el.parentElement;
    while (node && node !== document.body) {
      var overflow = window.getComputedStyle(node).overflowY;
      if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  /* Karta renderuje sie pod KOTWICA, czyli pod produktem, ktory uruchomil regule. Shopify
     wstawia nowo dodane pozycje na GORE koszyka, wiec z kazdym dodaniem kotwica schodzi
     o jedno miejsce nizej i karta wedruje na dol rosnacej listy. Przy dwoch-trzech dodatkach
     wypada poza widok — to jest zgloszenie klienta "nie widac kolejnych opcji". */
  function scrollSlotIntoView(context) {
    /* Slot wyszukujemy PRZY KAZDEJ probie, nigdy raz na poczatku. Kolejne renderowania
       podmieniaja `innerHTML` szuflady, wiec referencja z pierwszej proby wskazywalaby
       wezel odlaczony od dokumentu — jego `getBoundingClientRect()` to same zera i kod
       przewijalby w losowe miejsce.

       Slot MUSI byc z tego kontekstu, z ktorego dodawalismy. Poprzednia wersja brala
       `document.querySelector`, czyli pierwszy w DOM — a na stronie /cart pierwsza jest
       szuflada, ktora jest ukryta. Przewijanie jej slotu nie robilo nic widocznego. */
    function znajdzSlot() {
      var slots = document.querySelectorAll('[data-cart-upsell]');
      for (var i = 0; i < slots.length; i++) {
        if (slots[i].getAttribute('data-context') === context) return slots[i];
      }
      return slots[0] || null;
    }

    /* Przewijamy NATYCHMIASTOWO, nie plynnie. Lista koszyka zostala przed chwila zbudowana
       od zera, wiec nie ma ciaglosci, ktora plynny przesuw mialby pokazac — a animacja
       gryzlaby sie z ponawianiem ponizej. */
    function wysrodkuj() {
      var biezacy = znajdzSlot();
      if (!biezacy) return;
      try {
        var box = nearestScrollable(biezacy);
        if (!box) { biezacy.scrollIntoView({ block: 'center' }); return; }
        var br = box.getBoundingClientRect();
        var sr = biezacy.getBoundingClientRect();
        if (sr.top >= br.top && sr.bottom <= br.bottom) return; // juz widoczna w calosci
        box.scrollTop = box.scrollTop + (sr.top - br.top) - (box.clientHeight - sr.height) / 2;
      } catch (e) {
        try { biezacy.scrollIntoView(false); } catch (e2) {}
      }
    }

    /* Jednorazowe przewiniecie NIE WYSTARCZA i to jest sedno usterki zgloszonej przez klienta.
       Zmierzone w Chrome: pozycja liczona jest poprawnie i `scrollTop` sie ustawia, ale zaraz
       potem wraca do zera — inne skrypty motywu (m.in. pasek darmowej dostawy) reaguja na
       `cartUpdate` wlasnym renderowaniem i odtwarzaja liste koszyka od nowa. Dlatego
       ponawiamy przez ~1,4 s: kazdy przebieg sprawdza, czy karta jest w calosci widoczna,
       i tylko wtedy nie robi nic. Koszt to kilkanascie porownan prostokatow. */
    var proby = 0;
    function tick() {
      wysrodkuj();
      if (++proby < 12) window.setTimeout(tick, 120);
    }
    /* Dwie klatki na przeliczenie ukladu po podmianie `innerHTML` — sztywne 140 ms
       w poprzedniej wersji bylo zgadywaniem. */
    requestAnimationFrame(function () { requestAnimationFrame(tick); });
  }

  /* Flash the graduated line, move focus onto it (the clicked card is gone from the DOM, so
     focus would otherwise fall to <body>), announce in the theme's live region, and bring the
     fresh slot into view. Both surfaces expose the variant id on the quantity input. */
  function afterRender(context) {
    if (lastAddedVariantId) {
      try {
        var qty = document.querySelector('[data-quantity-variant-id="' + lastAddedVariantId + '"]');
        var line = qty
          ? (qty.closest('.cart-drawer__item') || qty.closest('.cart-item') || qty.closest('tr'))
          : null;
        if (line) {
          line.classList.add('cart-item--flash');
          window.setTimeout(function () {
            if (line) line.classList.remove('cart-item--flash');
          }, 1000);
          var name = line.querySelector('.cart-item__name');
          if (name && typeof name.focus === 'function') {
            try { name.focus({ preventScroll: true }); } catch (e) { name.focus(); }
          }
        }
      } catch (e) {}
    }
    try {
      var live = document.getElementById(context === 'page' ? 'cart-live-region-text' : 'CartDrawer-LiveRegionText');
      if (live && lastAddedText) live.textContent = lastAddedText;
    } catch (e) {}
    scrollSlotIntoView(context || 'drawer');
    lastAddedVariantId = null;
    lastAddedText = '';
  }

  function buildAddConfig() {
    var config = window.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    return config;
  }

  /* Dopisuje "<handle reguly>:<variantId>" do atrybutu koszyka `_upsell`. Atrybuty koszyka
     trafiaja do zamowienia jako customAttributes, skad appka liczy zamowienia i przychod
     na regule. Best-effort: kazdy blad jest polykany, bo statystyka nie moze nigdy zablokowac
     dodania produktu do koszyka. */
  function logAttribution(slot, variantId) {
    var handle = slot.getAttribute('data-rule-handle');
    if (!handle) return Promise.resolve();

    if (attributionLog === null) attributionLog = slot.getAttribute('data-upsell-log') || '';

    var entry = handle + ':' + variantId;
    var parts = attributionLog ? attributionLog.split(',') : [];
    if (parts.indexOf(entry) !== -1) return Promise.resolve();
    parts.push(entry);
    var next = parts.join(',');

    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(cartUrl + '/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _upsell: next } }),
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        attributionLog = (cart && cart.attributes && cart.attributes._upsell) || next;
      })
      .catch(function () {});
  }

  /* Dane do /cart/add.js. Znacznik pozycji promocyjnej: funkcja rabatu v3 rabatuje WYŁĄCZNIE
     linie z tą właściwością (jej wartość = handle reguły), a Liquid liczy po niej wykorzystane
     sztuki. Prefiks `_` ukrywa ją przed klientem w koszyku i w checkoucie. Ten sam wariant
     dodany z karty produktu ląduje jako osobna linia bez znacznika — i nie dostaje ceny promo. */
  function buildAddBody(slot, variantId) {
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    var ruleHandle = slot.getAttribute('data-rule-handle');
    if (ruleHandle) formData.append('properties[_upsell_rule]', ruleHandle);
    return formData;
  }

  /* Startuje żądanie OD RAZU po kliknięciu. Zwraca obietnicę odpowiedzi bramkowaną taktem
     animacji (`Promise.all` z `wait`), żeby potwierdzenie było widoczne co najmniej ADD_DELAY,
     ale nigdy nie wydłużało drogi do wyniku (zmierzone 2,2 s całości — 420 ms czekania
     PRZED fetchem to było ~20% z tego za nic). */
  function startAdd(slot, variantId, context) {
    var config = buildAddConfig();
    var formData = buildAddBody(slot, variantId);
    var toRender = [];
    if (context === 'page') {
      var cartItems = document.querySelector('cart-items');
      toRender = (cartItems && typeof cartItems.getSectionsToRender === 'function')
        ? cartItems.getSectionsToRender()
        : [];
      var sectionIds = toRender.map(function (s) { return s.section; });
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }
    }
    var request = fetch(window.routes.cart_add_url, { method: 'POST', headers: config.headers, body: formData })
      .then(function (res) { return res.json(); });
    return {
      response: Promise.all([request, wait(isReduced() ? 0 : ADD_DELAY)]).then(function (both) { return both[0]; }),
      toRender: toRender,
    };
  }

  /* ---- Drawer: mirror assets/product-complete-set.js (add → re-fetch section → swap) ---- */
  function addDrawer(slot, variantId, addBtn, responsePromise) {
    var cart = document.querySelector('cart-drawer');
    if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(addBtn);

    /* CELOWO NIE PROSIMY TU O `sections`: między dodaniem a renderem leci jeszcze
       `logAttribution`, które zmienia koszyk, więc sekcje z odpowiedzi `add` byłyby
       nieaktualne. Zmierzone na żywo 2026-08-18: samo `/cart/add` trwało 941 ms z ~2,2 s całości. */
    return responsePromise
      .then(function (response) {
        if (response.status) {
          publishError(slot, variantId, response);
          return;
        }
        lastAddedVariantId = String(variantId);
        var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        return logAttribution(slot, variantId)
          /* JEDNO zapytanie na obie sekcje zamiast dwóch po kolei (zmierzone 261 ms + 201 ms).
             Section Rendering API przyjmuje listę w `?sections=` i zwraca JSON `{ "nazwa": "<html>" }`. */
          .then(function () { return fetch(cartUrl + '?sections=cart-drawer,cart-icon-bubble'); })
          .then(function (r) { return r.json(); })
          .then(function (sectionsJson) {
            var html = (sectionsJson && sectionsJson['cart-drawer']) || '';
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var freshDrawer = doc.querySelector('#CartDrawer');
            var currentDrawer = document.querySelector('#CartDrawer');
            /* Podmiana `innerHTML` zeruje scroll kontenera, a na telefonie ucina też trwający
               gest — element pod palcem znika z dokumentu i przewijanie „przeskakuje" na
               stronę pod spodem (zgłoszenie klienta). Pozycję przywracamy po podmianie;
               scrollSlotIntoView i tak potem doceluje, ale bez skoku do zera po drodze. */
            var scrollBox = document.querySelector('#CartDrawer .cart-drawer__body');
            var keepScroll = scrollBox ? scrollBox.scrollTop : 0;
            if (freshDrawer && currentDrawer) currentDrawer.innerHTML = freshDrawer.innerHTML;
            scrollBox = document.querySelector('#CartDrawer .cart-drawer__body');
            if (scrollBox && keepScroll) scrollBox.scrollTop = keepScroll;

            var drawerInner = document.querySelector('cart-drawer .drawer__inner');
            if (drawerInner) drawerInner.classList.remove('is-empty');
            var cartDrawerEl = document.querySelector('cart-drawer');
            if (cartDrawerEl) {
              cartDrawerEl.classList.remove('is-empty');
              if (typeof cartDrawerEl.open === 'function') cartDrawerEl.open();
            }

            // Dymek koszyka przyszedł tym samym zapytaniem — bez drugiej podróży do serwera.
            var bubbleHtml = (sectionsJson && sectionsJson['cart-icon-bubble']) || '';
            if (bubbleHtml) {
              var bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
              var bubbleSrc = bubbleDoc.querySelector('.shopify-section') || bubbleDoc.body.firstElementChild;
              var bubbleTarget = document.getElementById('cart-icon-bubble');
              if (bubbleSrc && bubbleTarget) bubbleTarget.innerHTML = bubbleSrc.innerHTML;
            }
          })
          .then(function () {
            if (typeof publish === 'function') publish(PUB_SUB.cartUpdate, { source: 'cart-upsell' });
            afterRender('drawer');
          });
      })
      .catch(function () { publishError(slot, variantId, null); });
  }

  /* ---- Cart page: mirror assets/cart.js (add with sections → swap each section) ---- */
  function addPage(slot, variantId, responsePromise, toRender) {
    return responsePromise
      .then(function (response) {
        if (response.status) {
          publishError(slot, variantId, response);
          return;
        }
        lastAddedVariantId = String(variantId);
        return logAttribution(slot, variantId).then(function () {
          if (response.sections) {
            toRender.forEach(function (section) {
              var container = document.getElementById(section.id);
              if (!container) return;
              var target = container.querySelector(section.selector) || container;
              var sectionHtml = response.sections[section.section];
              if (!sectionHtml) return;
              target.innerHTML = getInnerHTML(sectionHtml, section.selector);
            });
          }
          if (typeof publish === 'function') publish(PUB_SUB.cartUpdate, { source: 'cart-upsell' });
          afterRender('page');
        });
      })
      .catch(function () { publishError(slot, variantId, null); });
  }

  /* ---- Click: beat + request at once; renders are queued ------------------------------
     Dwa szybkie kliknięcia w dwie karty = dwa równoległe żądania, ale JEDNA kolejka podmian
     sekcji: bez niej pierwsza odpowiedź przywracała drugą kartę do stanu „oferta" (serwer
     nie znał jeszcze drugiego dodania), a potem karta znikała. */
  var addChain = Promise.resolve();

  function onAddClick(slot) {
    if (!slot || slot.dataset.busy) return;
    slot.dataset.busy = '1';

    var addBtn = slot.querySelector('[data-upsell-add]');
    var variantId = slot.getAttribute('data-variant-id');
    var context = slot.getAttribute('data-context') || 'drawer';
    if (!variantId) { delete slot.dataset.busy; return; }

    haptic([10, 30, 14]);
    hideError(slot);
    setAddedState(slot, true);
    var nameEl = slot.querySelector('.cart-upsell__name');
    var confirm = slot.querySelector('.cart-upsell__confirm');
    lastAddedText = ((confirm ? confirm.textContent : '') + ' ' + (nameEl ? nameEl.textContent : '')).replace(/\s+/g, ' ').trim();

    var started = startAdd(slot, variantId, context);
    addChain = addChain
      .then(function () {
        return context === 'page'
          ? addPage(slot, variantId, started.response, started.toRender)
          : addDrawer(slot, variantId, addBtn, started.response);
      })
      .catch(function () {});
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-upsell-add]');
    if (!btn) return;
    var slot = btn.closest('[data-cart-upsell]');
    if (!slot) return;
    e.preventDefault();
    if (btn.getAttribute('aria-disabled') === 'true') return;
    onAddClick(slot);
  });

  /* ---- Nota po dopasowaniu koszyka ------------------------------------------------------
     Klient prosił o komunikat wprost, gdy ceny/ilości zmieniają się bez jego kliknięcia.
     Flaga w sesji przeżywa przeładowanie strony (na /cart plan wykonujemy z reloadem). */
  var GUARD_NOTE_FLAG = 'wingsUpsellGuardNote';

  function revealNotes() {
    var notes = document.querySelectorAll('[data-upsell-guard-note]');
    for (var i = 0; i < notes.length; i++) notes[i].hidden = false;
  }

  function showGuardNote() {
    var flag = null;
    try { flag = window.sessionStorage.getItem(GUARD_NOTE_FLAG); } catch (e) {}
    if (!flag) return;
    try { window.sessionStorage.removeItem(GUARD_NOTE_FLAG); } catch (e) {}
    revealNotes();
  }

  /* ---- Sprzątanie śladu atrybucji --------------------------------------------------
     `_upsell` ma odzwierciedlać koszyk, nie historię klikania. Klient zobaczył w adminie
     zamówienie z wpisem atrybucji bez produktu (kupujący dodał i usunął przed zapłatą)
     i zgłosił to jako błąd zliczania. Raport wpisy bez pokrycia i tak odfiltrowuje —
     to sprzątanie jest dla człowieka patrzącego w atrybuty zamówienia.

     Wpis w nieznanym formacie (bez dwukropka) zostaje — nie kasujemy, czego nie
     rozumiemy. Lustro tej reguły: `pruneUpsellValue` w appce
     (shared/models/checkoutOffersConfig.ts) — przykasa czyści identycznie. */
  var pruneBusy = false;
  function pruneAttribution(cart) {
    try {
      if (pruneBusy) return;
      var raw = cart && cart.attributes && cart.attributes._upsell;
      if (!raw) return;
      var inCart = {};
      (cart.items || []).forEach(function (item) { inCart[String(item.variant_id)] = 1; });
      var parts = String(raw).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      var kept = parts.filter(function (entry) {
        var sep = entry.indexOf(':');
        if (sep === -1) return true;
        return !!inCart[entry.slice(sep + 1)];
      });
      if (kept.length === parts.length) return;
      pruneBusy = true;
      var next = kept.join(',');
      var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      fetch(cartUrl + '/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _upsell: next } }),
      })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          /* Pamięć podręczna logu atrybucji musi zobaczyć nową wartość — inaczej
             następne dodanie odtworzyłoby skasowane wpisy z nieświeżej kopii. */
          attributionLog = (c && c.attributes && c.attributes._upsell) || next;
        })
        .catch(function () {})
        .then(function () { pruneBusy = false; });
    } catch (e) {}
  }

  /* Odświeża szufladę bez przeładowania strony — ta sama ścieżka, którą po dodaniu produktu
     idzie `addDrawer`.
     Stan otwarcia zapisujemy PRZED podmianą i przywracamy tylko wtedy, gdy szuflada była
     otwarta. Bezwarunkowe `open()` rozwijałoby ją klientowi, który jej wcale nie otwierał.
     Notę odsłaniamy PO podmianie, bo podmiana HTML kasuje wcześniejsze odsłonięcie. */
  function refreshDrawer() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    var el = document.querySelector('cart-drawer');
    var wasOpen = !!(el && el.classList.contains('active'));
    return fetch(cartUrl + '?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('#CartDrawer');
        var current = document.querySelector('#CartDrawer');
        // Ta sama ochrona scrolla co w addDrawer — patrz komentarz tam.
        var scrollBox = document.querySelector('#CartDrawer .cart-drawer__body');
        var keepScroll = scrollBox ? scrollBox.scrollTop : 0;
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        scrollBox = document.querySelector('#CartDrawer .cart-drawer__body');
        if (scrollBox && keepScroll) scrollBox.scrollTop = keepScroll;
        revealNotes();
        var drawer = document.querySelector('cart-drawer');
        if (wasOpen && drawer && typeof drawer.open === 'function') drawer.open();
      })
      .catch(function () {});
  }

  function onCartPage() {
    return /\/cart\/?$/.test(window.location.pathname || '');
  }

  /* ---- Plan korekty ---------------------------------------------------------------
     Liquid (snippets/cart-upsell-slot.liquid) liczy przy każdym renderze, ile sztuk promo
     przysługuje per reguła, i wystawia w `[data-cart-upsell-plan]` listę linii do przycięcia:
     nadmiar ponad uprawnienie (np. klient usunął walizkę przy dwóch kuferkach) oraz sieroty
     (znacznik bez aktywnej reguły). JS tylko wykonuje ten plan. Cena jest bezpieczna
     niezależnie od tego — funkcja rabatu nalicza najwyżej uprawnienie — więc to sprzątanie
     dla klienta, nie zabezpieczenie pieniędzy.

     Czytamy element z `data-context` zgodnym z powierzchnią. Na /cart ukryta szuflada ma
     własny, NIEPRZERYSOWYWANY przez Dawn element — gdyby był pierwszy w DOM, przycinalibyśmy
     według nieaktualnego planu (ta sama pułapka, która kiedyś gasiła strażnik kuponu). */
  var reconcileBusy = false;

  /* Po przycięciu w szufladzie notę odsłania `refreshDrawer`, ale zaraz potem `cartUpdate`
     każe Dawn (cart-drawer.js) wymienić `cart-drawer-items` świeżym renderem — z notą znów
     ukrytą. Dlatego przez kilka sekund po korekcie każde przerysowanie odsłania notę ponownie;
     sprawdzone na motywie dev 2026-10-30 (bez tego nota mignęła i znikła). */
  var noteUntil = 0;

  function planElement() {
    var want = onCartPage() ? 'page' : 'drawer';
    var all = document.querySelectorAll('[data-cart-upsell-plan]');
    for (var i = 0; i < all.length; i++) {
      if (all[i].getAttribute('data-context') === want) return all[i];
    }
    return null;
  }

  function changeLine(cartUrl, entry) {
    return fetch(cartUrl + '/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: entry.key, quantity: entry.quantity }),
    }).then(function (r) { return r.json(); });
  }

  function reconcile() {
    if (reconcileBusy) return;
    var plan = planElement();
    if (!plan) return;
    var trim;
    try { trim = JSON.parse(plan.getAttribute('data-trim') || '[]'); } catch (e) { return; }
    if (!trim || !trim.length) return;

    reconcileBusy = true;
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    var chain = Promise.resolve();
    trim.forEach(function (entry) {
      chain = chain.then(function () { return changeLine(cartUrl, entry); });
    });
    chain
      .then(function (cart) {
        /* Przycięte warianty mogły zniknąć — log atrybucji ma odzwierciedlać koszyk. */
        pruneAttribution(cart);
        try { window.sessionStorage.setItem(GUARD_NOTE_FLAG, '1'); } catch (e) {}
        noteUntil = Date.now() + 6000;
        if (onCartPage()) {
          window.location.reload();
          return;
        }
        return refreshDrawer().then(function () {
          /* Dymek, stopka i pasek darmowej dostawy subskrybują cartUpdate — bez tego
             zostałyby z liczbami sprzed przycięcia. Świeży render ma pusty plan, więc
             nie ma pętli z własnym przerysowaniem. */
          if (typeof publish === 'function') publish(PUB_SUB.cartUpdate, { source: 'cart-upsell' });
        });
      })
      .catch(function () {})
      .then(function () { reconcileBusy = false; });
  }

  /* ---- Wejście tylko dla NOWYCH kart ----------------------------------------------------
     Dawn buduje listę koszyka od nowa przy każdej zmianie ilości, więc animacja wejścia
     w CSS odpalała się na wszystkich kartach przy każdym przerysowaniu (szum, wrażenie
     „wolno"). Porównujemy handle reguł sprzed i po przerysowaniu per kontekst i klasę
     `cart-upsell--enter` dostają tylko karty, których wcześniej nie było. Pierwszy render
     strony tylko zapamiętujemy — strona już jest namalowana, animacja by mrugnęła.
     Wywoływane z obserwatora DOM (mikrotask, przed malowaniem), więc bez błysku. */
  var shownRules = { drawer: null, page: null };

  function markEnter() {
    ['drawer', 'page'].forEach(function (ctx) {
      var cards = document.querySelectorAll('[data-cart-upsell][data-context="' + ctx + '"]');
      var now = {};
      for (var i = 0; i < cards.length; i++) {
        var handle = cards[i].getAttribute('data-rule-handle') || ('#' + i);
        now[handle] = 1;
        if (shownRules[ctx] !== null && !shownRules[ctx][handle]) {
          cards[i].classList.add('cart-upsell--enter');
        }
      }
      shownRules[ctx] = now;
    });
  }

  /* Usunięcie pozycji przerysowuje sekcje koszyka AJAX-em, więc jednorazowe sprawdzenie przy
     wejściu nie wystarczy. Obserwator DOM działa niezależnie od tego, którą ścieżką motyw
     przerysuje koszyk. Koszt jest mały: czytamy atrybut z DOM, fetch idzie tylko gdy jest
     co przycinać (dawny strażnik kuponu pobierał sekcję i /cart.js przy KAŻDEJ mutacji). */
  var reconcileTimer = null;
  function scheduleReconcile() {
    if (reconcileTimer) window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(function () {
      reconcileTimer = null;
      if (Date.now() < noteUntil) revealNotes();
      reconcile();
    }, 250);
  }

  function init() {
    showGuardNote();
    /* Jednorazowe sprzątanie logu atrybucji przy wejściu — koszyk mógł się zmienić na innej
       karcie przeglądarki. Dalej czyścimy tylko po własnych zmianach. */
    fetch('/cart.js').then(function (r) { return r.json(); }).then(pruneAttribution).catch(function () {});
    markEnter();
    reconcile();
    if (typeof window.MutationObserver !== 'function') return;
    new window.MutationObserver(function () {
      markEnter();
      scheduleReconcile();
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
